import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useApolloClient } from '@apollo/client';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { Device } from '@oolio-group/domain';
import { Session } from '../../state/Session';
import { sessionSubject } from '../../state/sessionObservable';
import { setSession } from '../../state/preferences';
import { UPDATE_DEVICE_PAYMENT_TERMINAL } from '../../graphql/devices';
import { logCdsEvent } from '../../actionLogging/cdsAudit';
import { tap2PayEmbeddedManager } from '../../utils/Tap2PayEmbeddedManager';
import Sentry from '../../utils/Sentry/Sentry';
import {
  logVirtualTerminalSkipped,
  userIdFromToken,
  type StoredVirtualTerminal,
} from '../../utils/tap2PayVirtualTerminal';

const LINK_MAX_ATTEMPTS = 10;
const LINK_RETRY_DELAY_MS = 5_000;

const wait = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

export function useTap2PayEmbeddedInit(session: Partial<Session> | undefined) {
  const client = useApolloClient();
  const linkingRef = useRef<string | null>(null);

  const enabled =
    Platform.OS === 'android' &&
    !!session?.device?.enableTap2PayEmbedded &&
    !!session?.device?.linkCdsToPayApp;

  const storeRef = session?.currentStore?.payStoreRefs?.instore;
  const merchant = session?.currentStore?.merchantId;
  const accessToken = session?.token;
  const deviceId = session?.device?.id || '';

  const displaySettings = session?.device?.tap2PayDisplaySettings;
  const hasDisplaySettings = !!displaySettings;
  const enableRotation = displaySettings?.enableRotation;
  const nfcKioskPosition = displaySettings?.nfcKioskPosition;
  const pinAlignment = displaySettings?.pinAlignment;

  const linkPaymentTerminal = useCallback(
    async (terminal: StoredVirtualTerminal) => {
      const linkKey = `${terminal.deviceId}|${terminal.terminalId}`;
      if (linkingRef.current === linkKey) return;
      linkingRef.current = linkKey;
      const previousTerminal =
        sessionSubject.value.device?.paymentTerminal?.uuid;
      let lastError: unknown;
      try {
        for (let attempt = 1; attempt <= LINK_MAX_ATTEMPTS; attempt++) {
          const device = sessionSubject.value.device;
          if (!device?.id || device.id !== terminal.deviceId) return;
          if (!device.enableTap2PayEmbedded || !device.linkCdsToPayApp) return;
          if (device.paymentTerminal?.uuid === terminal.terminalId) return;
          try {
            const { data } = await client.mutate<{
              updateDevice: Pick<Device, 'id' | 'paymentTerminal'>;
            }>({
              mutation: UPDATE_DEVICE_PAYMENT_TERMINAL,
              variables: {
                input: {
                  id: device.id,
                  name: device.name,
                  paymentTerminal: terminal.terminalId,
                },
              },
            });
            const updated = data?.updateDevice;
            const latest = sessionSubject.value;
            if (updated && latest.device?.id === updated.id) {
              const updatedSession = {
                ...latest,
                device: { ...latest.device, ...updated },
              };
              sessionSubject.next(updatedSession);
              setSession(updatedSession);
            }
            return;
          } catch (error) {
            lastError = error;
            if (attempt < LINK_MAX_ATTEMPTS) await wait(LINK_RETRY_DELAY_MS);
          }
        }
        logCdsEvent({
          actionType: 'T2P_VIRTUAL_TERMINAL_FAILED',
          tag: AuditLogTags.PAYMENT,
          phrase: 'device payment terminal update failed',
          level: 'error',
          context: {
            deviceId: terminal.deviceId,
            previousTerminal,
            terminalId: terminal.terminalId,
            attempts: LINK_MAX_ATTEMPTS,
            errorMessage:
              lastError instanceof Error
                ? lastError.message
                : String(lastError),
          },
        });
        Sentry.captureException(lastError, {
          extra: {
            deviceId: terminal.deviceId,
            previousTerminal,
            terminalId: terminal.terminalId,
            attempts: LINK_MAX_ATTEMPTS,
            context: 'tap2pay-device-terminal-link-cds',
          },
        });
      } finally {
        if (linkingRef.current === linkKey) linkingRef.current = null;
      }
    },
    [client],
  );

  useEffect(() => {
    if (!enabled) return;

    if (!storeRef || !merchant || !accessToken) {
      Sentry.addBreadcrumb({
        category: 'tap2pay-embedded',
        message: 'Skipped init: missing storeRef/merchant/accessToken',
        level: 'warning',
        data: {
          hasStoreRef: !!storeRef,
          hasMerchant: !!merchant,
          hasAccessToken: !!accessToken,
        },
      });
      logVirtualTerminalSkipped(
        deviceId,
        Object.entries({ storeRef, merchant, accessToken })
          .filter(([, value]) => !value)
          .map(([name]) => name),
      );
      return;
    }

    tap2PayEmbeddedManager.setup({
      store: storeRef,
      merchant,
      accessToken,
      userId: userIdFromToken(accessToken),
      terminalId: '',
      deviceId,
      displaySettings: hasDisplaySettings
        ? {
            kioskModeEnabled: true,
            enableRotation,
            nfcKioskPosition,
            pinAlignment,
          }
        : null,
      onInitSuccess: durationMs => {
        logCdsEvent({
          actionType: 'T2P_SDK_INIT',
          tag: AuditLogTags.PAYMENT,
          phrase: 'card reader ready',
          durationMs,
          context: { storeRef, merchant },
        });
      },
      onVirtualTerminalError: error => {
        Sentry.captureException(error, {
          extra: {
            storeRef,
            merchant,
            deviceId,
            context: 'tap2pay-virtual-terminal-cds',
          },
        });
      },
      onVirtualTerminalReady: terminal => {
        void linkPaymentTerminal(terminal);
      },
      onInitError: error => {
        Sentry.captureException(error, {
          extra: {
            storeRef,
            merchant,
            context: 'tap2pay-embedded-init-cds',
          },
        });
        logCdsEvent({
          actionType: 'T2P_SDK_INIT_FAILED',
          tag: AuditLogTags.PAYMENT,
          phrase: 'card reader failed to start',
          level: 'error',
          context: {
            storeRef,
            merchant,
            errorMessage:
              error instanceof Error ? error.message : String(error),
          },
        });
      },
    });
  }, [
    enabled,
    storeRef,
    merchant,
    accessToken,
    deviceId,
    hasDisplaySettings,
    enableRotation,
    nfcKioskPosition,
    pinAlignment,
    linkPaymentTerminal,
  ]);
}
